import { type Routes } from '@angular/router';
import { AuthGuard } from '@core/guards/auth.guard';
import { NonAuthenticatedGuard } from '@core/guards/non-authenticated.guard';
import { AdminGuard } from '@core/guards/admin.guard';
import { type MTCIcons } from '@mtc/mtc-custom-icon/icons';
import { UserService } from '@services/user/user.service';
import { inject } from '@angular/core';
import { Rol } from '@core/models/rol.model';
import { MaintenanceGuard } from '@core/guards/maintenance.guard';

export enum APPRoutes {
  AUTH = 'auth',
  GROUPS = 'groups',
  SHIPS = 'ships',
  INVOICES = 'invoices',
  CHECK_IN = 'checkin',
  CHECK_IN_SINGLES = 's',
  FAVORITE_PASSENGERS = 'favorite-passengers',
  INVITATIONS = 'invitations',
  CHANGE_PASSWORD = 'change-password',
  DASHBOARD = 'dashboard',
  MAINTENANCE = 'maintenance',
}

export const routes = [
  {
    path: '',
    redirectTo: (): string => {
      const user = inject(UserService).user();
      return user?.isAdmin ? APPRoutes.DASHBOARD : APPRoutes.GROUPS;
    },
    pathMatch: 'full',
  },
  {
    path: APPRoutes.AUTH,
    title: 'Carnival - Groups System',
    canActivate: [NonAuthenticatedGuard],
    loadChildren: () => import('./features/auth/auth.routes'),
  },
  {
    path: APPRoutes.CHECK_IN,
    title: 'Check-In',
    loadChildren: () => import('./features/checkin/checkin.routes'),
  },
  {
    path: APPRoutes.CHECK_IN_SINGLES,
    title: 'Check-In',
    redirectTo: APPRoutes.CHECK_IN,
  },
  {
    path: APPRoutes.MAINTENANCE,
    canActivate: [MaintenanceGuard],
    title: 'Maintenance',
    loadComponent: () => import('./features/maintenance/maintenance.component'),
  },
  {
    path: '',
    loadComponent: () => import('./core/layouts/logged-in-layout/logged-in-layout.component'),
    canActivate: [AuthGuard],
    children: [
      {
        title: 'Dashboard',
        path: APPRoutes.DASHBOARD,
        loadComponent: () => import('./features/dashboard/dashboard.component'),
        data: { icon: 'home' as keyof typeof MTCIcons },
      },
      {
        title: 'Singles',
        path: APPRoutes.INVITATIONS,
        loadChildren: () => import('./features/invitations/invitations.routes'),
        data: {
          icon: 'user' as keyof typeof MTCIcons,
          breadcrumb: 'Singles',
        },
      },
      {
        title: 'Groups',
        path: APPRoutes.GROUPS,
        loadChildren: () => import('./features/groups/groups.routes'),
        data: { icon: 'passengers' as keyof typeof MTCIcons, breadcrumb: 'Groups' },
      },

      {
        title: 'Favorites',
        path: APPRoutes.FAVORITE_PASSENGERS,
        loadChildren: () => import('./features/favorite-passengers/favorite-passengers.routes'),
        data: { icon: 'star' as keyof typeof MTCIcons, breadcrumb: 'Favorites' },
      },
      {
        title: 'Ships',
        path: APPRoutes.SHIPS,
        canActivate: [AdminGuard],
        loadChildren: () => import('./features/ships/ships.routes'),
        data: { icon: 'ship' as keyof typeof MTCIcons, breadcrumb: 'Ships', canView: [Rol.ADMIN] },
      },
      {
        title: 'Invoices',
        path: APPRoutes.INVOICES,
        loadChildren: () => import('./features/invoices/invoices.routes'),
        data: { icon: 'dollarCircle' as keyof typeof MTCIcons, breadcrumb: 'Invoices' },
      },
      {
        title: 'Change Password',
        path: APPRoutes.CHANGE_PASSWORD,
        loadComponent: () => import('./features/change-password/change-password.component'),
        data: { hideInSidebar: true },
      },
    ],
  },
  {
    path: '**',
    redirectTo: APPRoutes.AUTH,
    pathMatch: 'full',
  },
] satisfies Routes;
